import { useMessages } from '@/i18n'
import { messages } from './messages'
import { messages as settingsMessages } from '../../design/messages'
import type { DesignBackdrop, DesignElement } from '@/types/blocks'
import { Plus } from 'lucide-react'
import {
  DESIGN_LIMITS,
  badgeItems,
  newBadgeItem,
  tableRows,
} from '@/features/editor/lib/designElements'
import {
  addTableColumn,
  addTableRow,
  removeTableColumn,
  removeTableRow,
  tableColumnCount,
} from '@/features/editor/lib/designTable'
import { BADGE_VARIANT_OPTIONS, resolveBadgeVariant } from '@/features/editor/lib/badgeVariants'
import { ALIGN_OPTIONS } from '../../design/segmented'
import { QBtn, QDropdown, QSep, QStepper } from './quickToolbarParts'
import { useQuickToolbar } from './quickToolbarContext'
import { QuickTextFormat } from './QuickTextFormat'
import { useTextSelection } from './useTextSelection'
import { ImageActions } from './ImageActions'

type BackdropKey =
  'backdropNone' | 'backdropLight' | 'backdropDark' | 'backdropPrimary' | 'backdropBlur'

const BACKDROP_KEYS: Array<{ value: DesignBackdrop; key: BackdropKey }> = [
  { value: 'none', key: 'backdropNone' },
  { value: 'light', key: 'backdropLight' },
  { value: 'dark', key: 'backdropDark' },
  { value: 'primary', key: 'backdropPrimary' },
  { value: 'blur', key: 'backdropBlur' },
]

const deg = (v: number) => (v > 0 ? `+${v}°` : `${v}°`)

type Patch = Partial<DesignElement>

const Align = ({ value, onUpdate }: { value: string; onUpdate: (p: Patch) => void }) => (
  <>
    {ALIGN_OPTIONS.map(({ value: v, icon: Icon, title }) => (
      <QBtn
        key={v}
        title={title ?? v}
        active={value === v}
        onClick={() => onUpdate({ textAlign: v })}
      >
        {Icon ? <Icon className="size-4" /> : null}
      </QBtn>
    ))}
  </>
)

const Sizes = ({
  options,
  value,
  onUpdate,
}: {
  options: Array<{ value: DesignElement['textSize']; label: string }>
  value: DesignElement['textSize']
  onUpdate: (p: Patch) => void
}) => {
  const m = useMessages(messages)
  const { preview } = useQuickToolbar()
  return (
    <QDropdown
      title={m.textSize}
      value={value}
      options={options}
      onChange={(textSize) => onUpdate({ textSize })}
      onPreview={(textSize) => preview(textSize ? { textSize } : null)}
    />
  )
}

/**
 * Быстрые настройки прямо над выделенным элементом составного блока — те же поля
 * и значения, что в панели справа (`HeadingSettings`/`TextSettings`/…), ради
 * правки без перевода взгляда. Тексты, бейджи-списки и картинку панель держит сама.
 */
export const DesignQuickToolbar = ({
  element,
  onUpdate,
}: {
  element: DesignElement
  onUpdate: (patch: Patch) => void
}) => {
  const m = useMessages(messages)
  const sm = useMessages(settingsMessages)
  const { preview } = useQuickToolbar()
  const { hasSelection } = useTextSelection()
  // Выделен кусок текста — в панели только форматирование выделения.
  if (element.type === 'text' && hasSelection) return <QuickTextFormat separated={false} />
  switch (element.type) {
    case 'heading':
      return (
        <>
          <Align value={element.textAlign} onUpdate={onUpdate} />
          <QSep />
          <Sizes
            options={[
              { value: 'large', label: 'L' },
              { value: 'xlarge', label: 'XL' },
            ]}
            value={element.textSize}
            onUpdate={onUpdate}
          />
        </>
      )
    case 'text': {
      return (
        <>
          <Align value={element.textAlign} onUpdate={onUpdate} />
          <QSep />
          <Sizes
            options={[
              { value: 'medium', label: 'S' },
              { value: 'large', label: 'M' },
              { value: 'xlarge', label: 'L' },
            ]}
            value={element.textSize}
            onUpdate={onUpdate}
          />
          <QSep />
          <QDropdown
            title={m.backdrop}
            value={element.backdrop}
            options={BACKDROP_KEYS.map(({ value, key }) => ({ value, label: sm[key] }))}
            onChange={(backdrop) => onUpdate({ backdrop })}
            onPreview={(backdrop) => preview(backdrop ? { backdrop } : null)}
          />
          <QSep />
          <QStepper
            label={m.tilt}
            value={element.textRotation}
            display={deg(element.textRotation)}
            min={-DESIGN_LIMITS.TEXT_ROTATION}
            max={DESIGN_LIMITS.TEXT_ROTATION}
            onChange={(textRotation) => onUpdate({ textRotation } as Patch)}
          />
        </>
      )
    }
    case 'image':
      return (
        <>
          <QStepper
            label={m.tilt}
            value={element.imageRotation}
            display={deg(element.imageRotation)}
            min={-DESIGN_LIMITS.IMAGE_ROTATION}
            max={DESIGN_LIMITS.IMAGE_ROTATION}
            onChange={(imageRotation) => onUpdate({ imageRotation } as Patch)}
          />
          <QSep />
          <ImageActions element={element} onUpdate={onUpdate} />
        </>
      )
    case 'badge': {
      const active = resolveBadgeVariant(element.badgeVariant)
      return (
        <>
          <span className="flex items-center gap-0.5">
            {BADGE_VARIANT_OPTIONS.map(({ value, label }) => (
              <QBtn
                key={value}
                title={m.styleOf(label)}
                active={active === value}
                onClick={() => onUpdate({ badgeVariant: value } as Patch)}
              >
                {label}
              </QBtn>
            ))}
          </span>
          <QSep />
          {/* Новый бейдж — текст и иконка правятся прямо в пилюле на холсте. */}
          <QBtn
            title={m.addBadge}
            disabled={badgeItems(element.badges).length >= DESIGN_LIMITS.BADGES}
            onClick={() =>
              onUpdate({ badges: [...badgeItems(element.badges), newBadgeItem('')] } as Patch)
            }
          >
            <Plus className="size-4" />
            <span className="ml-1">{m.badge}</span>
          </QBtn>
        </>
      )
    }
    case 'table': {
      const data = tableRows(element.tableData)
      const rows = data.length
      const cols = tableColumnCount(data)
      return (
        <>
          <QStepper
            label={m.rows}
            value={rows}
            display={m.rowsShort(rows)}
            min={DESIGN_LIMITS.TABLE_ROWS_MIN}
            max={DESIGN_LIMITS.TABLE_ROWS_MAX}
            onChange={(n) =>
              onUpdate({ tableData: n > rows ? addTableRow(data) : removeTableRow(data) } as Patch)
            }
          />
          <QSep />
          <QStepper
            label={m.columns}
            value={cols}
            display={m.columnsShort(cols)}
            min={DESIGN_LIMITS.TABLE_COLS_MIN}
            max={DESIGN_LIMITS.TABLE_COLS_MAX}
            onChange={(n) =>
              onUpdate({
                tableData: n > cols ? addTableColumn(data) : removeTableColumn(data),
              } as Patch)
            }
          />
        </>
      )
    }
  }
}
